import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./style.css";

interface UserCategory {
  title: string;
  description: string;
}

interface UserCategoryProps {
  title: string;
  subtitle: string;
  categories: UserCategory[];
}

const fadeUpVariants = {
  hidden: { opacity: 0, y: 100 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.5 } },
};

const UserCategorySection: React.FC<UserCategoryProps> = ({
  title,
  subtitle,
  categories,
}) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <Container className="my-5 text-center">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={fadeUpVariants}
      >
        <h2 className="gradient-text mb-3" style={{ fontSize: "2.2rem", fontWeight: 800 }}>{title}</h2>
        <p className="mb-4 mx-auto" style={{ color: "#94a3b8", fontSize: "1rem", maxWidth: "800px" }}>{subtitle}</p>

        <Row className="justify-content-center g-3">
          {categories.map((item, index) => (
            <Col md={6} lg={4} className="mb-3 d-flex" key={index}>
              <div
                className="glass-card p-4 flex-grow-1 d-flex flex-column text-start"
                style={{
                  background: "rgba(30, 41, 59, 0.6)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  borderRadius: "20px",
                }}
              >
                <h4 style={{ color: "#f8fafc", fontSize: "1.2rem", fontWeight: 700, marginBottom: "8px" }}>
                  {item.title}
                </h4>
                <p className="flex-grow-1 mb-0" style={{ color: "#94a3b8", fontSize: "0.95rem", lineHeight: 1.6 }}>
                  {item.description}
                </p>
              </div>
            </Col>
          ))}
        </Row>
      </motion.div>
    </Container>
  );
};


const singleFaceSwapCategories = [
  {
    title: "✔ Social Media Users",
    description: "Create viral content for Instagram, TikTok, and Snapchat.",
  },
  {
    title: "✔ Meme Lovers",
    description: "Swap faces to make funny and trending memes.",
  },
  {
    title: "✔ Content Creators",
    description: "Produce high-quality photo edits for blogs and social media.",
  },
  {
    title: "✔ Friends & Family",
    description:
      "Have fun swapping faces with loved ones and share the laughs.",
  },
];

const multiFaceSwapCategories = [
  {
    title: "✔ Families",
    description: "Swap faces in family portraits for laughs!",
  },
  {
    title: "✔ Couples",
    description: "Try couple swaps to see a whole new look.",
  },
  {
    title: "✔ Friends",
    description: "Turn group selfies into hilarious face-swapped edits.",
  },
  {
    title: "✔ Social Media Creators",
    description: "Create trending content with group face swaps.",
  },
  {
    title: "✔ Meme Lovers",
    description: "Make viral content with crazy face combinations.",
  },
];

// Use the reusable component
export const WhoCanUseSnapToon: React.FC = () => (
  <UserCategorySection
    title="Who Can Use SwapToon?"
    subtitle="SwapToon is for everyone! Whether you’re a casual user looking for fun, a content creator making engaging posts, or a professional editor exploring new creative possibilities, SwapToon makes face swapping effortless and enjoyable."
    categories={singleFaceSwapCategories}
  />
);

const preweddingCategories = [
  {
    title: "✔ Engaged Couples",
    description: "Preview pre-wedding concepts, poses, and attire before your actual photo shoot.",
  },
  {
    title: "✔ Married Couples",
    description: "Recreate romantic pre-wedding moments or try luxury traditional attire.",
  },
  {
    title: "✔ Wedding Planners & Stylists",
    description: "Showcase mood boards and attire ideas to couples visually.",
  },
  {
    title: "✔ Social Media Creators",
    description: "Share aesthetic couple portraits, save-the-date cards, and reels.",
  },
];

export const WhoCanUseMultiFaceSwap: React.FC = () => (
  <UserCategorySection
    title="Who Can Use Multi-Face Swap?"
    subtitle="This feature is great for anyone who loves fun photo transformations:"
    categories={multiFaceSwapCategories}
  />
);

export const WhoCanUsePrewedding: React.FC = () => (
  <UserCategorySection
    title="Who Can Use Pre-Wedding Face Swap?"
    subtitle="Ideal for couples, wedding enthusiasts, and event creators looking for dream romantic photos:"
    categories={preweddingCategories}
  />
);

const babyshowerCategories = [
  {
    title: "✔ Expecting Parents",
    description: "Create joyful baby shower and maternity photos to celebrate the upcoming addition to your family.",
  },
  {
    title: "✔ Family & Relatives",
    description: "Swap faces in group photos with grandparents, siblings, and aunts/uncles.",
  },
  {
    title: "✔ Event Hosts & Planners",
    description: "Design custom invitation banners, welcome boards, and digital memory books.",
  },
  {
    title: "✔ Social Media & Photographers",
    description: "Share heartwarming pregnancy announcements and festive baby shower edits.",
  },
];

export const WhoCanUseBabyshower: React.FC = () => (
  <UserCategorySection
    title="Who Can Use Baby Shower Face Swap?"
    subtitle="Perfect for expecting parents, families, and event organizers celebrating new arrivals:"
    categories={babyshowerCategories}
  />
);
